import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl-next';
import { usePathParam } from 'react-resource-router';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { LoadingButton } from '@atlaskit/button';
import Form from '@atlaskit/form';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Text } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { Flex } from '@atlassian/eoc-flex';
import { useResponderNames } from '@atlassian/eoc-responder-names';

import { type FormValues, type RuleType } from '../../types';
import { isResponderDeleted } from '../../utils';

import { EscalationRulesInputs } from './escalation-rules-inputs';
import messages from './messages';
import { NameInputs } from './name-inputs';
import { RepeatEscalationInputs } from './repeat-escalation-inputs';
import { SectionMessageContainer } from './styled';
import { TeamInput } from './team-input';
import { type EscalationPolicyModalProps } from './types';

export const EscalationPolicyModal = ({
	isOpen,
	onClose,
	onSubmit,
	notifyTypes,
	escalation,
	isCloning,
	escalationNames,
	hasRepeatRight,
	isLoading,
	returnFocusRef,
}: EscalationPolicyModalProps) => {
	const { formatMessage } = useIntl();
	const [teamId] = usePathParam('teamId');

	// We need deleted entity count since there can be same deleted responders for different rules.
	const [deletedEntityCount, setDeletedEntityCount] = useState(0);
	const [isProcessing, setIsProcessing] = useState(false);

	const requestedResponders = escalation?.rules
		.map((rule) => ({
			id: rule.notifyId,
			type: rule.type,
		}))
		.filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i);

	const responders = useResponderNames(requestedResponders ?? []);
	const InitialDeletedResponders = responders.filter(isResponderDeleted);
	const InitialDeletedEntities =
		escalation?.rules.filter(
			(rule) =>
				InitialDeletedResponders.findIndex((responder) => responder.id === rule.notifyId) !== -1,
		) ?? [];

	// escalations is undefined in the inial render, it is set when edit/clone modal is opened.
	useEffect(() => {
		setDeletedEntityCount(InitialDeletedEntities.length);
	}, [InitialDeletedEntities.length]);

	const getModalTitle = () => {
		if (isCloning) {
			return formatMessage(messages.cloneTitle);
		}

		if (escalation) {
			return formatMessage(messages.editTitle);
		}

		return formatMessage(messages.createTitle);
	};

	const handleClose = (analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'closeEscalationPolicyModal');

		if (onClose) {
			onClose();
		}
	};

	const handleSubmit = ({ team, ...values }: FormValues) => {
		if (isProcessing) {
			return {
				rules: 'is-processing',
			};
		}

		if (values.rules.length === 0) {
			return {
				rules: 'required',
			};
		}

		values.teamId = teamId || team?.atlassianIdentifier;

		onSubmit(values);
	};

	const checkRule = (rule: RuleType) => {
		if (InitialDeletedEntities.includes(rule)) {
			setDeletedEntityCount((prev) => prev - 1);
		}
	};

	return (
		<ModalTransition>
			{isOpen && (
				<Modal
					onClose={(_, analyticEvent) => handleClose(analyticEvent)}
					width="large"
					shouldReturnFocus={returnFocusRef}
				>
					<Form<FormValues> onSubmit={handleSubmit}>
						{({ formProps }) => (
							<form id="escalation-policy" {...formProps}>
								<ModalHeader>
									<ModalTitle>{getModalTitle()}</ModalTitle>
								</ModalHeader>
								{deletedEntityCount > 0 && (
									<SectionMessageContainer>
										<SectionMessage appearance="error">
											<Text as="strong">
												{formatMessage(messages.infoSection, {
													deletedEntityCount: deletedEntityCount,
												})}
											</Text>
										</SectionMessage>
									</SectionMessageContainer>
								)}
								<ModalBody>
									<Flex flexDirection="column" rowGap={3}>
										<Flex flexDirection="column" rowGap={2}>
											<NameInputs
												name={escalation?.name}
												description={escalation?.description}
												isCloning={isCloning}
												escalationNames={escalationNames}
											/>
											{!teamId && <TeamInput teamId={escalation?.teamId} />}
										</Flex>
										<EscalationRulesInputs
											rules={escalation?.rules}
											notifyTypes={notifyTypes}
											onChange={checkRule}
											onDelete={checkRule}
											onProcess={(isProcessing) => setIsProcessing(isProcessing)}
										/>
										{hasRepeatRight && (
											<RepeatEscalationInputs
												repeat={escalation?.repeat}
												maxRepeatCount={escalation?.maxRepeatCount}
												closeAlertWhenRepeatsCompleted={escalation?.closeAlertWhenRepeatsCompleted}
												unAckWhileRepeating={escalation?.unAckWhileRepeating}
											/>
										)}
									</Flex>
								</ModalBody>
								<ModalFooter>
									<Button
										appearance="subtle"
										onClick={(_, analyticsEvent) => handleClose(analyticsEvent)}
									>
										{formatMessage(messages.cancel)}
									</Button>
									<LoadingButton isLoading={isLoading} type="submit" appearance="primary">
										{isCloning
											? formatMessage(messages.clone)
											: formatMessage(escalation ? messages.update : messages.create)}
									</LoadingButton>
								</ModalFooter>
							</form>
						)}
					</Form>
				</Modal>
			)}
		</ModalTransition>
	);
};
